/**
 * Angular Import
 */
import { Injectable } from '@angular/core';
/**
 * Module Import
 */
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
/**
 * Environment Import
 */
import { environment } from './../../../environments/environment';
/**
 * Service Import
 */
import { PostApiService } from '../post-api.service';
import { FunCollectionService } from './../../common/common-functions/fun-collection.service';

/**
 * Constant imports.
 */
import { APP_PERMISSION_ACTION, PRODUCT_TYPE_CONSTANT_NAME, SAMVAAD_INDEX_DB, SAMVAAD_NOTIFICATION } from '../../../app/constants/app.constants';
import { ChatService } from 'src/app/samvaad/chat/lib/chat.service';

/**
 * sdk imports
 */
import { Analytics } from 'analytics2.0';
import { APP_PERMISSIONS } from 'analytics2.0/dist/core/constants/analytic.interface';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public message: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private db: any;

  constructor(private postApi: PostApiService, private funCollectionService: FunCollectionService, private samNotifyService: ChatService, private analyticSdk: Analytics) { }
  requestPermission(): void {
    Notification.requestPermission().then((permission) => {
      const existingPermsision = localStorage.getItem('notificationPermission');
      let toFireAnalytics = false;
      if (permission != existingPermsision) {
        toFireAnalytics = true;
      }
      let appPermsion: APP_PERMISSIONS = {
        entity_type: PRODUCT_TYPE_CONSTANT_NAME.OTHER,
        entity_code: 0,
        action_on: APP_PERMISSION_ACTION.ALLOWED,
        type: 'NOTIFICATION',
        start_time: new Date().getTime()
      }
      if (permission === 'granted') {
        localStorage.setItem('notificationPermission', permission);
        const messaging = getMessaging();
        getToken(messaging,
         { vapidKey: environment.firebase.vapidKey}).then(
           (currentToken) => {
             if (currentToken) {
               localStorage.setItem('deviceToken', currentToken);
               this.postApi.registerDevice(currentToken).subscribe(() =>{
                // No Code
               });
             }
         }).catch(() => {
          // No Code
         });
      }
      else {
        localStorage.setItem('notificationPermission', permission);
        appPermsion.action_on = APP_PERMISSION_ACTION.DENIED;
      }
      if (toFireAnalytics == true) {
        this.analyticSdk.analyticService.APP_PERMISSIONS(appPermsion);
      }
    });

  }
  /**
   * Listen Notification
   */
  listen(): void {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      const additionalInfo = JSON.parse(payload?.data?.additional_info);
      if (this.samNotifyService.isSamvaadNotification(additionalInfo?.isSamvaadRequest)) {
        const isP2p = this.checkIsP2P(additionalInfo.topic);
        if (!additionalInfo.silent) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.checkDatabaseExists().then((event: any) => {
            if (event) {
              this.db = event.target.result;
              this.getUserInfoFromIndexDB(additionalInfo, payload, isP2p);
              this.closeIndexDb();
            } else {
              payload = this.samvaadGrouping(payload, additionalInfo, isP2p);
              this.funCollectionService.showWebPushNoti(payload);
              this.message = payload;
              this.closeIndexDb();
            }
          });
        }
      } else {
        this.funCollectionService.showWebPushNoti(payload);
        this.message = payload;
      }
    });
  }

  checkIsP2P(topic): boolean {
    return topic.substring(0, 3) === 'usr';
  }

  checkDatabaseExists(): Promise<boolean | Event> {
    return new Promise((resolve) => {
      const dbName = SAMVAAD_INDEX_DB;
      const request = indexedDB.open(dbName);

      request.onerror = () => {
        resolve(false);
      };

      request.onsuccess = (event) => {
        // request.result.close(); // Close the database connection.
        resolve(event);
      };

      request.onupgradeneeded = () => {
        // Database doesn't exist.
        resolve(false);
      };
    });
  }

  /**
   * Send samvaad notification with grouping.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  samvaadGrouping(payload, additionalInfo, isP2p): any {
    payload = this.samNotifyService.getSamvadNotification(payload?.data, additionalInfo, isP2p);
    const groupId = payload.data.target_url;
    if (this.funCollectionService.notifyGrouping[groupId]) {
      const length = Object.keys(this.funCollectionService.notifyGrouping[groupId]).length;
      if (length >= SAMVAAD_NOTIFICATION.LIMIT) {
        const oldestKey = Object.keys(this.funCollectionService.notifyGrouping[groupId])[0];
        delete this.funCollectionService.notifyGrouping[groupId][oldestKey];
      }
      this.funCollectionService.notifyGrouping[groupId][SAMVAAD_NOTIFICATION.MAX_SEQ - additionalInfo.seq] = payload.data.message;
      payload.data.message = this.funCollectionService.notifyGrouping[groupId];
      this.funCollectionService.closeNotification();
    } else {
      this.funCollectionService.notifyGrouping[groupId] = { [SAMVAAD_NOTIFICATION.MAX_SEQ - additionalInfo.seq]: payload.data.message };
      payload.data.message = this.funCollectionService.notifyGrouping[groupId];
    }
    return payload;
  }

  closeIndexDb(): void {
    this.db.close();
  }

  /**
   * Get chat user info from index DB.
   */
  getUserInfoFromIndexDB(additionalInfo, payload, isP2p): void {
    if (this.db) {
      const objectStoreNames = this.db.objectStoreNames;
      if (objectStoreNames.contains('topic')) {
        const transaction = this.db.transaction(['topic'], 'readonly');
        const objectStore = transaction.objectStore('topic');
        const request = objectStore.get(additionalInfo.topic);
        if (!isP2p) {
          const requestFrom = objectStore.get(additionalInfo.xfrom);
          requestFrom.onsuccess = function (event) {
            const userInfo = event.target.result;
            if (userInfo && userInfo?.public) {
              additionalInfo.fullName = `${userInfo?.public?.name ? userInfo?.public?.name : `${userInfo?.public?.fn ? userInfo?.public?.fn : ''} ${userInfo?.public?.ln ? userInfo?.public?.ln : ''}`}`;
            }
          };
        }
        request.onsuccess = (event) => {
          const userInfo = event.target.result;
          if (userInfo) {
            if (userInfo && userInfo?.acs?.mode.indexOf('B') > -1) {
              if (userInfo?.public) {
                if (!isP2p) {
                  additionalInfo.name = `${userInfo?.public?.name ? userInfo?.public?.name : `${userInfo?.public?.fn ? userInfo?.public?.fn : ''} ${userInfo?.public?.ln ? userInfo?.public?.ln : ''}`}`;
                } else {
                  additionalInfo.fullName = `${userInfo?.public?.name ? userInfo?.public?.name : `${userInfo?.public?.fn ? userInfo?.public?.fn : ''} ${userInfo?.public?.ln ? userInfo?.public?.ln : ''}`}`;
                }
                payload.data.title = !isP2p ? additionalInfo.name : additionalInfo.fullName;
              }
            } else {
              return;
            }
          }
          payload = this.samvaadGrouping(payload, additionalInfo, isP2p);
          this.funCollectionService.showWebPushNoti(payload);
          this.message = payload;
          this.closeIndexDb();
        };

        request.onerror = () => {
          payload = this.samvaadGrouping(payload, additionalInfo, isP2p);
          this.funCollectionService.showWebPushNoti(payload);
          this.message = payload;
          this.closeIndexDb();
        };
      }
    }
  }
}

